/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-07 13:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, TextStyle, Card, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { MobileCancelMajor } from '@shopify/polaris-icons';
import sadEmoji from 'src/assets/images/dashboard/expReviewRating/modal/sadEmoji.png';
import loveEmoji from 'src/assets/images/dashboard/expReviewRating/modal/loveEmoji.png';
import { reduxForm, Field, getFormValues, reset } from 'redux-form';
import { issuesChoices } from 'src/constants/subscription';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { hasTag, isExceedLength, isEmail } from 'src/helpers/form-validation-helper';
import * as commonAction from 'actions/common-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';

const isExceedLengthValue = isExceedLength(10000);
const isEmailValue = isEmail();

type Props = {
  active: boolean,
  deleting: boolean,
  title: string,
  content: string,
  subContent: string,
  handleCancel: () => void,
  isTrialPeriod: boolean
};

type ComposedProps = Props & WithI18nProps;
// DONE
class UnsubscribeFeedbackModal extends Component<ComposedProps> {
  state = {
    modalContent: '',
    clickingButton: false // tracking open popup by clicking floating button
  };

  componentWillReceiveProps(nextProps) {
    const { openFeedback, resetForm, resetSurveyFeedback } = nextProps;
    if (openFeedback) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      resetForm();
      resetSurveyFeedback();
    }
  }
  render() {
    const { openFeedback, sendSuccess } = this.props;

    return (
      <div>
        {openFeedback && (
          <div id="BC-UnsubscribeModal-Backdrop">
            <div id="BC-UnsubscribeModal-Content" className={sendSuccess ? 'success' : ''}>
              <Button
                id="BC-ExpReviewRatingModal-Button"
                onClick={this.handleCancel}
                plain
                icon={MobileCancelMajor}></Button>
              <div className="feedback">
                <div className="topImage">
                  <img src={sendSuccess ? loveEmoji : sadEmoji} alt="" />
                </div>
                <Card>
                  <Card.Section>{this.renderFeedbackForm()}</Card.Section>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  renderFeedbackForm = () => {
    const { i18n, pristine, submitting, sending, issueList, handleSubmit, addonName, sendSuccess } =
      this.props;

    const formattedChoices = issuesChoices.map(choice => ({
      ...choice,
      label: i18n.translate(choice.label)
    }));

    const titleModal = sendSuccess
      ? i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.titleSuccessFeedback')
      : i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.title', {
          t1: addonName
        });

    return (
      <div>
        <p style={{ paddingBottom: '8px', fontSize: '16px' }}>
          <TextStyle variation="strong">
            {titleModal}
          </TextStyle>
        </p>

        {sendSuccess ? (
          <div style={{ paddingBottom: '8px' }}>
            <p>
              {i18n.translate(
                'AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.subTitleSuccessFeedback_1'
              )}
            </p>
            <p>
              {i18n.translate(
                'AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.subTitleSuccessFeedback_2'
              )}
            </p>
          </div>
        ) : (
          <>
            <p style={{ paddingBottom: '4px' }}>
              {i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.subTitle01')}
            </p>
            <p style={{ paddingBottom: '8px' }}>
              {i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.subTitle02')}
            </p>
          </>
        )}

        {!sendSuccess && (
          <form onSubmit={this.handleDefaultSubmit}>
            <p>{i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.content')}</p>
            <FormLayout>
              <Field
                name="issueList"
                choices={formattedChoices}
                component={RenderChoice}
                anotherProps={{ allowMultiple: 'true' }}
              />
              {issueList.includes('others') && (
                <div style={{ maxHeight: '200px' }}>
                  <Field
                    name="content"
                    component={RenderTextField}
                    placeholder={i18n.translate(
                      'AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.reasonOther_Placeholder'
                    )}
                    anotherProps={{ multiline: 3 }}
                    validate={[hasTag, isExceedLengthValue]}
                  />
                </div>
              )}
              <Field
                name="submitterEmail"
                label={
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.textFieldEmail_Label'
                    )}
                  </TextStyle>
                }
                component={RenderTextField}
                placeholder={i18n.translate(
                  'AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.textFieldEmail_Placeholder'
                )}
                validate={[isEmailValue, isExceedLengthValue]}
              />
            </FormLayout>
          </form>
        )}

        <div>
          {sendSuccess ? (
            <Button primary onClick={this.handleCancel}>
              {i18n.translate('AdminHTML.SubscriptionV3.UnsubscribeFeedbackModal.gotIt_Button')}
            </Button>
          ) : (
            <Button
              primary={issueList.length !== 0}
              disabled={issueList.length === 0 || pristine || submitting}
              loading={sending && !pristine}
              onClick={handleSubmit(this.handleSubmitFeedback.bind(this))}>
            {i18n.translate('AdminHTML.Common.Action.submitFeedback')}
            </Button>
          )}
        </div>
      </div>
    );
  };
  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Handle submit feedback
  handleSubmitFeedback = data => {
    // const {  since = 0 } = this.state;
    const { i18n, version, addonName, isTrialPeriod } = this.props;
    const { content = '', issueList = [], submitterEmail = '' } = data;

    const selections = _(issueList)
      .map(issue => {
        const choice = _.find(issuesChoices, { value: issue });
        if (!choice) return null;
        const { label, code, tracking } = choice;
        const description = label.includes('AdminHTML') ? i18n.translate(label) : label;
        return {
          code,
          tracking,
          description
        };
      })
      .compact()
      .orderBy('code', 'asc')
      .value();

    const messageArray = [];
    let message = `* Addon name: ${addonName} \n  `;
    message += selections
      .map(selection => {
        const { code, description } = selection;
        if (code === '6') {
          messageArray.push(`${description}${content ? `: ${content}` : ''}`);
          return `${code}/ ${description} - ${content}`;
        }
        messageArray.push(description);
        return `${code}/ ${description}`;
      })
      .join('\n    ');

    // submit feedback
    this.submitFeedback({
      message,
      submitterEmail,
      version,
      feature: 'add-on',
      addonName: addonName,
      messageArray,
      isTrialPeriod
    });
  };

  submitFeedback = (body: Object) => {
    const { sendSurveyFeedback } = this.props;
    sendSurveyFeedback(body);
    this.props.getDetailInvoice();
    this.props.getAddons();
  };

  // Close modal
  handleCancel = () => {
    this.props.handleFeedbackModal();
    this.props.getDetailInvoice();
    this.props.getAddons();
  };
}

const mapStateToProps = (state, props) => {
  const { common } = state;
  const formValues = getFormValues('UnsubscribeFeedbackModalForm')(state);

  return {
    sending: _.get(common, 'sending', false),
    sendSuccess: _.get(common, 'sendSuccess', false),
    issueList: _.get(formValues, 'issueList', [])
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('UnsubscribeFeedbackModalForm')),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  getAddons: () => dispatch(subscriptionV3Actions.getAddons(ownProps.i18n)),
  sendSurveyFeedback: data => {
    dispatch(commonAction.sendSurveyFeedback(data, ownProps.i18n));
  },
  resetSurveyFeedback: () => {
    dispatch(commonAction.resetSurveyFeedback());
  }
});

const UnsubscribeFeedbackModalForm = reduxForm({
  form: 'UnsubscribeFeedbackModalForm', // a unique identifier for this form
  enableReinitialize: true
})(UnsubscribeFeedbackModal);

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(UnsubscribeFeedbackModalForm)
);
